import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Tab,
  Tabs,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  People,
  Business,
  Favorite,
  AttachMoney,
  Timeline,
  AutoAwesome
} from '@mui/icons-material';
import { 
  AreaChart, 
  Area, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import UserManagement from './UserManagement';
import PropertyAnalytics from './PropertyAnalytics';
import AIInsights from './components/AIInsights';
import MetricCard from './components/MetricCard';
import PropertyTypeProgress from './components/PropertyTypeProgress';
import { growthTrends as mockGrowthTrends } from './data/mockData';
import apiClient from '../../services/apiClient';
import adminService from '../../services/adminService';
import styles from './styles/AdminDashboard.module.css';

const AdminDashboard = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [growthTrends, setGrowthTrends] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  // Calculate property types from real data
  const calculatePropertyTypes = (properties) => {
    if (!properties || properties.length === 0) return [];

    // Group properties by county for meaningful distribution
    const countyCount = {};
    const totalProperties = properties.length;

    properties.forEach(property => {
      const county = property.county || 'Unknown';
      countyCount[county] = (countyCount[county] || 0) + 1;
    });

    // Get top 4 counties and convert to percentages
    return Object.entries(countyCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 4)
      .map(([county, count]) => ({
        type: county.replace(' County', ''),
        percentage: Math.round((count / totalProperties) * 100)
      }));
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch admin metrics and properties in parallel
        // Removed debug logging
        const [adminData, propertiesResponse] = await Promise.all([
          adminService.getDashboardMetrics().catch(() => null),
          apiClient.get('/listings', { params: { limit: 10000 } }).catch(() => ({ data: [] }))
        ]);

        // Removed debug logging
        // Extract properties data from response
        const properties = propertiesResponse.data?.data || propertiesResponse.data || [];
        // Removed debug logging

        // Set admin metrics
        if (adminData) {
          setDashboardMetrics(adminData);
          setGrowthTrends(adminData.growthTrends || mockGrowthTrends);
        } else {
          setDashboardMetrics({
            totalUsers: 0,
            activeUsers: 0,
            totalProperties: properties.length || 0,
            savedProperties: 0,
            usersWithFavorites: 0,
            recentFavorites: 0
          });
          setGrowthTrends(mockGrowthTrends);
        }

        // Calculate property types from real data
        const calculatedTypes = calculatePropertyTypes(properties);
        setPropertyTypes(calculatedTypes);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        // Use fallback data
        setDashboardMetrics({
          totalUsers: 0,
          activeUsers: 0,
          totalProperties: 0,
          savedProperties: 0,
          usersWithFavorites: 0,
          recentFavorites: 0
        });
        setGrowthTrends(mockGrowthTrends);
        setPropertyTypes([
          { type: 'Gloucester', percentage: 35 },
          { type: 'Bergen', percentage: 30 },
          { type: 'Hudson', percentage: 25 },
          { type: 'Others', percentage: 10 }
        ]);
        setError('Unable to fetch real-time data. Showing available information.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderMetricCards = () => {
    const isLoadingMetrics = loading || !dashboardMetrics;
    
    const metrics = [
      {
        title: "Total Users",
        value: dashboardMetrics?.totalUsers || 0,
        icon: People,
        trend: 12,
        color: "#1976d2"
      },
      {
        title: "Active Users",
        value: dashboardMetrics?.activeUsers || 0,
        icon: TrendingUp,
        trend: 8,
        color: "#2e7d32"
      },
      {
        title: "Total Properties",
        value: dashboardMetrics?.totalProperties || 0,
        icon: Business,
        trend: 15,
        color: "#ed6c02"
      },
      {
        title: "Saved Properties",
        value: dashboardMetrics?.savedProperties || 0,
        icon: Favorite,
        trend: 23,
        color: "#d32f2f"
      },
      {
        title: "Users with Favorites",
        value: dashboardMetrics?.usersWithFavorites || 0,
        icon: AttachMoney,
        trend: 18,
        color: "#7b1fa2"
      },
      {
        title: "Recent Activity",
        value: dashboardMetrics?.recentFavorites || 0,
        icon: Timeline,
        trend: 5,
        color: "#1565c0"
      }
    ];
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              icon={metric.icon}
              trend={metric.trend}
              color={metric.color}
              isLoading={isLoadingMetrics}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCharts = () => {
    const chartData = growthTrends.length > 0 ? growthTrends : mockGrowthTrends;
    
    // Prepare pie chart data for county distribution
    const pieChartData = propertyTypes.map((item, index) => ({
      name: item.type,
      value: item.percentage,
      color: ['#1976d2', '#ed6c02', '#2e7d32', '#9c27b0', '#d32f2f', '#1565c0'][index % 6]
    }));
    
    return (
      <Grid container spacing={3}>
        {/* Growth Trends */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={1}
            sx={{ 
              p: 3, 
              height: 400,
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
                Platform Growth Trends
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly user and property growth
              </Typography>
            </Box>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <defs>
                  <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="propertiesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ed6c02" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ed6c02" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#1976d2"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#usersGradient)"
                  name="New Users"
                />
                <Area
                  type="monotone"
                  dataKey="properties"
                  stroke="#ed6c02"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#propertiesGradient)"
                  name="Properties"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* County Distribution */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={1}
            sx={{ 
              p: 3, 
              height: 400,
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
                County Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Properties by county
              </Typography>
            </Box>
            
            <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="white"
                    strokeWidth={2}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [`${value}%`, 'Properties']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={1}>
                {pieChartData.map((item, index) => (
                  <Grid item xs={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          backgroundColor: item.color,
                          borderRadius: 1,
                          mr: 1,
                          flexShrink: 0
                        }} 
                      />
                      <Typography variant="caption" color="text.secondary">
                        {item.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" className={styles.dashboardContainer}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading dashboard data...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Remove the blocking error check since we now use fallback data

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" fontWeight="bold">
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive analytics and management tools for your foreclosure dealfinder platform
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{ 
            '& .MuiTab-root': { 
              minWidth: 120,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Users" />
          <Tab label="Properties" />
          <Tab label="AI Analytics" />
        </Tabs>
      </Box>

      {/* Tab Content with proper spacing */}
      <Box sx={{ mt: 3 }}>
        {tabValue === 0 && (
          <Box>
            {renderMetricCards()}
            {renderCharts()}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <UserManagement />
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <PropertyAnalytics />
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <AIInsights />
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AdminDashboard; 